import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Apple, RefreshCw, Sparkles, Coffee, Droplet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((l, i) => {
    const isBullet = /^\s*[-•*]/.test(l);
    const cleaned = l.replace(/^\s*[-•*]\s?/, "");
    const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div key={i} className={`flex gap-2 ${isBullet ? "items-start" : ""}`}>
        {isBullet && <span className="text-amber-400 mt-1.5 shrink-0">•</span>}
        <span className="leading-relaxed">
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**") ? (
              <strong key={j} className="text-amber-200 font-semibold">{p.slice(2, -2)}</strong>
            ) : (
              <span key={j}>{p}</span>
            )
          )}
        </span>
      </div>
    );
  });
}

export default function NutritionRecommendations() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/ai/nutrition").then((r) => setText(r.data?.content || "")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Apple className="w-6 h-6 text-amber-300" /> Nutrition around your sleep windows
          </h2>
          <p className="text-slate-400 text-sm mt-1">Meal timing, pre-sleep foods, caffeine strategy & hydration — tailored to your shifts.</p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          data-testid="btn-regenerate-nutrition"
          variant="outline"
          className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Regenerate
        </Button>
      </div>

      <div className="circa-card p-6 sm:p-8" data-testid="nutrition-content">
        <div className="pill text-amber-300 border-amber-500/30 bg-amber-950/50 mb-5">
          <Sparkles className="w-3.5 h-3.5" /> Personalized · Next 24h
        </div>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4 bg-white/10" />
            <Skeleton className="h-4 w-2/3 bg-white/10" />
            <Skeleton className="h-4 w-4/5 bg-white/10" />
            <Skeleton className="h-4 w-1/2 bg-white/10" />
          </div>
        ) : (
          <div className="space-y-3 text-slate-200 text-sm sm:text-base">
            {renderMarkdown(text)}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <NutriTile icon={Coffee} tint="text-rose-300" title="Caffeine cutoff" body="Stop caffeine 8 hours before your anchor sleep." />
        <NutriTile icon={Apple} tint="text-emerald-300" title="Pre-sleep snack" body="Small tryptophan snack 60-90 min before bed if hungry: oats, yogurt, banana." />
        <NutriTile icon={Droplet} tint="text-sky-300" title="Hydration" body="Front-load fluids; taper 90 min pre-sleep to reduce night wake-ups." />
      </div>
    </section>
  );
}

function NutriTile({ icon: Icon, tint, title, body }) {
  return (
    <div className="circa-card p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${tint}`} />
        <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">{title}</div>
      </div>
      <div className="text-slate-200 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
